// src/pages/Home/components/LiveRates.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Clock } from 'lucide-react';
import '../Home.css';

const LiveRates = ({ rates, loading, activeTab, setActiveTab, silverRates, goldRates }) => {
  const [previousPrices, setPreviousPrices] = useState({});
  const priceChangeRef = useRef({});
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());

  // Track previous prices for color change
  useEffect(() => {
    const currentPrices = {};
    const allRates = activeTab === 'silver' ? silverRates : goldRates;
    
    allRates.forEach(rate => {
      if (rate.id) {
        currentPrices[rate.id] = rate.price;
      }
    });
    
    // Check if prices changed
    let hasChanges = false;
    Object.keys(currentPrices).forEach(id => {
      if (previousPrices[id] !== undefined && previousPrices[id] !== currentPrices[id]) {
        hasChanges = true;
        priceChangeRef.current[id] = currentPrices[id] > previousPrices[id] ? 'up' : 'down';
      }
    });
    
    // If new items added, mark as neutral
    Object.keys(currentPrices).forEach(id => {
      if (previousPrices[id] === undefined) {
        priceChangeRef.current[id] = 'neutral';
      }
    });
    
    if (hasChanges || Object.keys(previousPrices).length === 0) {
      setPreviousPrices(currentPrices);
      setLastUpdateTime(new Date());
    }
  }, [silverRates, goldRates, activeTab, previousPrices]);

  // Get MCX rate (Silver Spot or Gold Spot)
  const getMCXRate = () => {
    const allRates = activeTab === 'silver' ? silverRates : goldRates;
    return allRates.find(r => 
      r.name?.toLowerCase().includes('spot') ||
      r.purity === 'Spot'
    );
  };

  // Get USD/INR rate
  const getUSDRate = () => {
    return rates?.find(r => 
      r.name?.toLowerCase().includes('usd inr') ||
      r.name?.toLowerCase().includes('usd/inr')
    );
  };

  // Get Costing rate (Silver Costing or Gold Costing)
  const getCostingRate = () => {
    const allRates = activeTab === 'silver' ? silverRates : goldRates;
    return allRates.find(r => 
      r.name?.toLowerCase().includes('costing')
    );
  };

  const mcxRate = getMCXRate();
  const usdRate = getUSDRate();
  const costingRate = getCostingRate();

  // Get price change for top cards
  const getTopCardChange = (rateId, currentPrice) => {
    if (priceChangeRef.current[rateId]) {
      return priceChangeRef.current[rateId];
    }
    if (previousPrices[rateId] === undefined) return 'neutral';
    if (currentPrice > previousPrices[rateId]) return 'up';
    if (currentPrice < previousPrices[rateId]) return 'down';
    return 'neutral';
  };

  if (loading && rates.length === 0) {
    return (
      <div className="container-custom">
        <div className="rates-loading">
          <div className="loader">
            <div className="loader-spinner" />
            <span>Loading live rates...</span>
          </div>
        </div>
      </div>
    );
  }

  // Get products (filter out Spot, Costing, USD)
  const getProducts = () => {
    const allRates = activeTab === 'silver' ? silverRates : goldRates;
    return allRates.filter(r => 
      r.purity && 
      r.purity !== 'Spot' &&
      !r.name?.toLowerCase().includes('costing') &&
      !r.name?.toLowerCase().includes('usd') &&
      r.price > 0 // Filter out zero prices
    );
  };

  const products = getProducts();

  const getPriceChange = (rateId, currentPrice) => {
    if (priceChangeRef.current[rateId]) {
      return priceChangeRef.current[rateId];
    }
    if (previousPrices[rateId] === undefined) return 'neutral';
    if (currentPrice > previousPrices[rateId]) return 'up';
    if (currentPrice < previousPrices[rateId]) return 'down';
    return 'neutral';
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return '--';
    return price.toLocaleString('en-IN', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    });
  };

  return (
    <motion.section 
      className="live-rates-section"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container-custom">
        <div className="section-header">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            📊 Live Market Rates
          </motion.h2>
          <div className="header-right">
            <span className="currency-label">🇮🇳 INR</span>
            <span className="live-indicator">
              <span className="live-dot" />
              Live
            </span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <motion.button
            className={`tab-btn ${activeTab === 'silver' ? 'active' : ''}`}
            onClick={() => setActiveTab('silver')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">🥈</span>
            Silver
          </motion.button>
          <motion.button
            className={`tab-btn ${activeTab === 'gold' ? 'active' : ''}`}
            onClick={() => setActiveTab('gold')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">🥇</span>
            Gold
          </motion.button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="tab-content"
          >
            {/* Top Row: 3 Columns */}
            <div className="rates-top-row">
              {/* Column 1: MCX Rate */}
              <motion.div 
                className={`rate-card-top ${getTopCardChange(mcxRate?.id, mcxRate?.price)}`}
                whileHover={{ y: -4, boxShadow: 'var(--shadow-lg)' }}
              >
                <div className="card-header">
                  <span className="card-icon">📈</span>
                  <span className="card-label">MCX {activeTab === 'silver' ? 'Silver' : 'Gold'}</span>
                </div>
                <div className="card-value">
                  {mcxRate ? (
                    `₹${formatPrice(mcxRate.price)}`
                  ) : (
                    '--'
                  )}
                </div>
                <div className="card-sub">
                  per {mcxRate?.unit || 'oz'}
                  {getTopCardChange(mcxRate?.id, mcxRate?.price) !== 'neutral' && (
                    <span className={`price-change-badge ${getTopCardChange(mcxRate?.id, mcxRate?.price)}`}>
                      {getTopCardChange(mcxRate?.id, mcxRate?.price) === 'up' ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Column 2: USD/INR Rate */}
              <motion.div 
                className={`rate-card-top ${getTopCardChange(usdRate?.id, usdRate?.price)}`}
                whileHover={{ y: -4, boxShadow: 'var(--shadow-lg)' }}
              >
                <div className="card-header">
                  <span className="card-icon">💵</span>
                  <span className="card-label">USD/INR</span>
                </div>
                <div className="card-value">
                  {usdRate ? (
                    `₹${formatPrice(usdRate.price)}`
                  ) : (
                    '--'
                  )}
                </div>
                <div className="card-sub">
                  per USD
                  {getTopCardChange(usdRate?.id, usdRate?.price) !== 'neutral' && (
                    <span className={`price-change-badge ${getTopCardChange(usdRate?.id, usdRate?.price)}`}>
                      {getTopCardChange(usdRate?.id, usdRate?.price) === 'up' ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Column 3: Costing Rate (Highlighted) */}
              <motion.div 
                className={`rate-card-top highlight ${getTopCardChange(costingRate?.id, costingRate?.price)}`}
                whileHover={{ y: -4, boxShadow: 'var(--shadow-lg)' }}
              >
                <div className="card-header">
                  <span className="card-icon">⚡</span>
                  <span className="card-label">{activeTab === 'silver' ? 'Silver' : 'Gold'} Costing</span>
                </div>
                <div className="card-value">
                  {costingRate && costingRate.price > 0 ? (
                    `₹${formatPrice(costingRate.price)}`
                  ) : (
                    '--'
                  )}
                </div>
                <div className="card-sub">
                  per {costingRate?.unit || 'kg'}
                  {getTopCardChange(costingRate?.id, costingRate?.price) !== 'neutral' && costingRate?.price > 0 && (
                    <span className={`price-change-badge ${getTopCardChange(costingRate?.id, costingRate?.price)}`}>
                      {getTopCardChange(costingRate?.id, costingRate?.price) === 'up' ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Bottom Row: Product Table */}
            <div className="rates-bottom-row">
              <motion.div 
                className="rate-card-bottom"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="bottom-card-content">
                  <div className="bottom-card-header">
                    <span className="bottom-card-icon">
                      {activeTab === 'silver' ? '🔶' : '🌟'}
                    </span>
                    <span className="bottom-card-title">
                      {activeTab === 'silver' ? 'Silver' : 'Gold'} Products
                    </span>
                    <span className="product-count">
                      {products.length} items
                    </span>
                    <span className="update-time">
                      Updated: {lastUpdateTime.toLocaleTimeString()}
                    </span>
                  </div>
                  
                  {products.length > 0 ? (
                    <div className="product-table-wrapper">
                      {/* Table Header */}
                      <div className="product-table-header">
                        <div className="header-cell name-cell">Product Name</div>
                        <div className="header-cell buy-cell">Buy (H)</div>
                        <div className="header-cell sell-cell">Sell (L)</div>
                      </div>

                      {/* Table Body */}
                      <div className="product-table-body">
                        {products.map((product, index) => {
                          const change = getPriceChange(product.id, product.price);
                          const buyPrice = product.price;
                          const sellPrice = product.ask || product.price * 1.005;
                          
                          return (
                            <motion.div 
                              key={product.id || index}
                              className={`product-table-row ${change}`}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.03 }}
                              whileHover={{ 
                                backgroundColor: 'var(--bg-hover)',
                                transition: { duration: 0.2 }
                              }}
                            >
                              {/* Product Name Column */}
                              <div className="row-cell name-cell">
                                <div className="product-info">
                                  <span className="product-purity-badge">
                                    {product.purity || 'N/A'}
                                  </span>
                                  <span className="product-name-text">
                                    {product.name || product.metal}
                                  </span>
                                  {product.weight && (
                                    <span className="product-weight-text">
                                      {product.weight}
                                    </span>
                                  )}
                                  <span className="product-unit-text">
                                    {product.unit || 'gm'}
                                  </span>
                                </div>
                                {change !== 'neutral' && (
                                  <span className={`row-change-indicator ${change}`}>
                                    {change === 'up' ? '▲' : '▼'}
                                    {product.change || ''}
                                  </span>
                                )}
                              </div>

                              {/* Buy Column with High */}
                              <div className="row-cell buy-cell">
                                <div className="price-container">
                                  <span className={`buy-price ${change}`}>
                                    ₹{formatPrice(buyPrice)}
                                  </span>
                                  <span className="high-low-label">
                                    H: ₹{formatPrice(product.high || buyPrice * 1.02)}
                                  </span>
                                </div>
                              </div>

                              {/* Sell Column with Low */}
                              <div className="row-cell sell-cell">
                                <div className="price-container">
                                  <span className="sell-price">
                                    ₹{formatPrice(sellPrice)}
                                  </span>
                                  <span className="high-low-label">
                                    L: ₹{formatPrice(product.low || sellPrice * 0.98)}
                                  </span>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="no-products-message">
                      <Activity size={32} />
                      <p>No {activeTab === 'silver' ? 'silver' : 'gold'} products available</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="rate-footer">
          <p className="rate-source">
            <Clock size={14} className="inline" />
            Developer Darshan Jain | 9974706814 | jdarshan535@gmail.com
          </p>
        </div>
      </div>
    </motion.section>
  );
};

export default LiveRates;